/**
 * InsightDetailModal Component
 *
 * Modal showing full insight details:
 * - Full content (formatted)
 * - All metadata (type, agent, project, files, tags, severity)
 * - Delete button with confirmation
 */

import { useState } from 'react';
import {
  X,
  Trash2,
  FileText,
  User,
  Folder,
  Tag,
  AlertTriangle,
  Sparkles,
  Calendar,
  Loader2,
} from 'lucide-react';

function TypeBadge({ type }) {
  const colors = {
    'bug-fix': '#ef4444',
    'pattern': '#10b981',
    'preference': '#f59e0b',
    'learning': '#6366f1',
    'decision': '#8b5cf6',
  };
  const color = colors[type] || '#6b7280';

  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium"
      style={{
        backgroundColor: `${color}20`,
        color: color,
        border: `1px solid ${color}40`,
      }}
    >
      {type}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const colors = {
    low: 'text-gray-400 bg-gray-500/10 border-gray-500/30',
    medium: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    high: 'text-red-400 bg-red-500/10 border-red-500/30',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[severity] || colors.medium}`}>
      {severity}
    </span>
  );
}

export default function InsightDetailModal({
  insight,
  onClose,
  onDelete,
  isDeleting,
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!insight) return null;

  const handleDelete = async () => {
    await onDelete(insight.id);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">Insight Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {/* Type & Severity Row */}
          <div className="flex items-center gap-3 mb-4">
            <TypeBadge type={insight.type} />
            <SeverityBadge severity={insight.severity || 'medium'} />
            {(insight.hasEmbedding || insight.embedding) && (
              <span className="flex items-center gap-1 text-xs text-amber-400">
                <Sparkles className="w-3 h-3" />
                Embedding
              </span>
            )}
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Agent:</span>
              <span className="text-gray-200">
                {insight.agentName || insight.agent?.displayName || insight.agent?.name || '-'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Folder className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Project:</span>
              <span className="text-gray-200">
                {insight.projectName || insight.project?.displayName || insight.project?.name || '-'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Created:</span>
              <span className="text-gray-200">
                {insight.createdAt
                  ? new Date(insight.createdAt).toLocaleString('de-DE')
                  : '-'}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Content</h3>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
              <p className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">
                {insight.content}
              </p>
            </div>
          </div>

          {/* Files */}
          {insight.files && insight.files.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Files</h3>
              <div className="flex flex-wrap gap-2">
                {insight.files.map((file, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-700/50 rounded text-xs text-gray-300 font-mono"
                  >
                    {file}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {insight.tags && insight.tags.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {insight.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-indigo-500/10 border border-indigo-500/30 rounded text-xs text-indigo-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-between items-center">
          <div className="text-xs text-gray-500 font-mono">
            ID: {insight.id}
          </div>

          {showDeleteConfirm ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-400">Delete this insight?</span>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded-md disabled:opacity-50 flex items-center gap-1"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Confirm
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-md"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3 py-1.5 bg-gray-700 hover:bg-red-600/20 hover:text-red-400 text-gray-400 text-sm rounded-md flex items-center gap-1 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
